import { useState, useEffect, useMemo } from "react";
import AgGridModalLayout from "@wrappers/layouts/AgGridModalLayout";
import { useSelector, useDispatch } from "react-redux";
import {
    setAddAsExactData,
    setIsSearchTermExact,
    updateAsinId,
    updateCustomModal,
    updateKeywordModalType,
    updateKeywords,
    updateProductModalType,
} from "@features/modalSlice";
import { Button, Modal } from "antd";
import { createDatasource } from "@ts/retrieve-data";
import {
    bulkActionChangeSubmit,
    titleCase,
    agBtnExport,
    onKeywordSelectionChanged,
    onProductAdGroupsSelectionChanged,
    getActiveModule,
    numericValueFormatter,
    onSearchTermCampaignSelectionChanged,
    handleAddExactBtnClick,
    isTarget,
    isPropPresent,
    getGridType,
} from "@ts/utils";
import { getDateObj } from "@ts/date-range";
import * as Sentry from "@sentry/react";
import KeywordsBulkOperationsBtns from "@buttons/KeywordsBulkOperationsBtns";
import { updateKeywordsSelectionData } from "@features/selectedDataSlice";
import {
    ColDef,
    GridApi,
    RowNode,
    ValueFormatterParams,
} from "ag-grid-community";
import {
    Channels,
    ColorHex,
    IChannel,
    ICustomCellRendererParams,
    ParentModule,
    ProductModalType,
    TCommonRowData,
    TKeywordsSelectionData,
} from "@ts/common-types";
import {
    useGetBulkActionBtnConfig,
    useGetModulesData,
} from "@/hooks/AllCustomHooks";
import {
    DISABLED_MATCH_TYPES,
    KEYWORD_MATCH_TYPES,
    customFilterOptions,
} from "@/ts/constants";
import {
    StatusBadgeCellRenderer,
    ChangeInValueCellRenderer,
    FilterTextFormatter,
    ProgramTypeCellRenderer,
    StatusCellRenderer,
    KeywordAddBidCellRenderer,
    UpdateBidOrBudgetCellRenderer,
} from "../cell-renderers/AllCellRenderers";
import { FaPause, FaPlay, FaTrashAlt } from "react-icons/fa";
import { AiOutlineClose } from "react-icons/ai";
import { isNull } from "underscore";
import RemoveEntityCellRenderer from "../cell-renderers/RemoveEntityCellRenderer";
import ExportGridBtn from "../buttons/ExportGridBtn";

interface IShow {
    customModalDisp: {
        value: boolean;
    };
}

type TValue = {
    value: string;
};

type TValueBool = {
    value: boolean;
};

interface IKeyword {
    value: string[];
}

interface ISearchTermCampaigns {
    value: number[];
}

export interface IModal {
    activeModule?: string;
    productModalType?: TValue;
    keywordModalType?: TValue;
    customModalTitle?: TValue;
    asinId?: TValue;
    keywords?: IKeyword;
    searchTermCampaigns?: ISearchTermCampaigns;
    isSearchTermExact?: TValueBool;
    customModalDisp?: TValueBool;
    addToExactModalDisp?: TValueBool;
}

const CustomModal: React.FC = () => {
    const [gridApi, setGridApi] = useState<GridApi>();
    const productModalType = useSelector(
        (state: IModal) => state.productModalType.value
    );
    const keywordModalType = useSelector(
        (state: IModal) => state.keywordModalType.value
    );
    const { activeModule } = getActiveModule();
    const customModalTitle = useSelector(
        (state: IModal) => state.customModalTitle.value
    );
    const addAsExactData = useSelector(
        (state: TKeywordsSelectionData) => state.addAsExactData.value
    );

    const show = useSelector((state: IShow) => state.customModalDisp.value);
    const asin = useSelector((state: IModal) => state.asinId.value);
    const keywords = useSelector((state: IModal) => state.keywords.value);
    const isSearchTermExact = useSelector(
        (state: IModal) => state.isSearchTermExact.value
    );
    const searchTermCampaigns = useSelector(
        (state: IModal) => state.searchTermCampaigns.value
    );
    const [rowData, setRowData] = useState<TCommonRowData>();
    const [loading, setLoading] = useState<boolean>(true);
    const [gridHeight, setGridHeight] = useState<number>();
    const channel = useSelector((state: IChannel) => state.channel.value);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const addAsExactConfig = useGetBulkActionBtnConfig();
    const exactLoader = addAsExactConfig["exact"].loader;
    const negativeLoader = addAsExactConfig["negativeExact"].loader;

    const dispatch = useDispatch();
    const handleClose = () => {
        dispatch(updateProductModalType(""));
        dispatch(updateKeywordModalType(""));
        dispatch(updateCustomModal(false));
        dispatch(updateKeywordsSelectionData([]));
        dispatch(updateAsinId(""));
        dispatch(updateKeywords([]));
        setRowData(null);
        dispatch(setAddAsExactData([]));
        dispatch(setIsSearchTermExact(false));
    };

    const gridParams = useGetModulesData({
        type: "grid-params",
        subGridType: getGridType(),
    });

    const keywordsModalColDefs: ColDef[] = [
        {
            field: "keyword_name",
            headerName: "Target",
            minWidth: 200,
            filter: "agTextColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["text"],
                valueFormatter: function (params: ValueFormatterParams) {
                    return titleCase(params.value || "");
                },
            },
            pinned: "left",
            lockPinned: true,
            cellClass: "lock-pinned",
            checkboxSelection: true,
            tooltipField: "keyword_name",
            valueFormatter: function (params: ValueFormatterParams) {
                return titleCase(params.value || "");
            },
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            cellRenderer: StatusBadgeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                pinnedColumnValue: "Total",
                isBadgeRequired: !!gridParams?.isBadgeRequired,
            },
        },
        {
            field: "bid",
            headerName: "Bid",
            pinned: "left",
            minWidth: isPropPresent(gridParams, "bid")
                ? gridParams["bid"].minWidth
                : 150,
            maxWidth: isPropPresent(gridParams, "bid")
                ? gridParams["bid"].maxWidth
                : 170,
            lockPinned: true,
            filter: false,
            tooltipValueGetter: ({ data: { sys_ams_keyword_id, bid } }) => {
                if (sys_ams_keyword_id === "00000000000000")
                    return `${bid} !operation unavailable`;
                else return bid;
            },
            cellClass: "lock-pinned",
            cellRenderer: isPropPresent(gridParams, "bid")
                ? gridParams["bid"].cellRenderer
                : UpdateBidOrBudgetCellRenderer,
            cellRendererParams: {
                isModalComponent: true,
            },
        },
        {
            field: "state",
            headerName: "Bid Status",
            pinned: "left",
            maxWidth: 100,
            lockPinned: true,
            ...(gridParams?.canChangeKeywordStatus
                ? {
                      cellRenderer: (params: ICustomCellRendererParams) =>
                          StatusCellRenderer(params, "keyword"),
                  }
                : {
                      valueFormatter: function (params: ValueFormatterParams) {
                          return titleCase(params.value || "");
                      },
                      hide: true,
                  }),
            cellClass: "lock-pinned",
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isModalComponent: true,
            },
        },
        {
            field: "kwd_class",
            headerName: "Keyword Class",
            filter: "agSetColumnFilter",
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            valueFormatter: (params: ValueFormatterParams) =>
                params.value ? titleCase(params.value) : params.value,
            filterParams: {
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value ? titleCase(params.value) : params.value,
            },
        },
        // {
        // 	field: "keyword_status",
        // 	headerName: "Keyword Status",
        // 	pinned: "left",
        // 	maxWidth: 116,
        // 	lockPinned: true,
        // 	cellRenderer: keywordNatureCellRenderer, // To add input field in update bid column
        // 	tooltipField: "keyword_status",
        // 	cellClass: "lock-pinned",
        // 	// cellStyle: { "white-space": "normal" },
        // 	filter: false,
        // 	pinnedRowCellRenderer: blankCellRenderer,
        // },
        {
            field: "ad_group_name",
            headerName: "Ad Group",
            filter: "agTextColumnFilter",

            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            headerName: "Impressions",
            field: "impression",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
            },
            hide: true,
        },
        {
            headerName: gridParams["click"]?.headerName || "Clicks",
            field: "click",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
            },
            hide: true,
        },
        {
            headerName: "Spends",
            field: "spend",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            sort: "desc",
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            field: "latest_top_viewed_pos",
            headerName: "Latest Position",
            minWidth: 90,
            maxWidth: 100,
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: false,
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "avg_top_viewed_pos",
            headerName: "Avg Position",
            minWidth: 90,
            maxWidth: 100,
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: false,
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "search_volume",
            headerName: "Search Vol .",
            minWidth: 120,
            maxWidth: 130,
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,
            cellRenderer: ChangeInValueCellRenderer,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: false,
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "sponsored_sov",
            headerName: "Sov",
            minWidth: 120,
            maxWidth: 130,
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,
            cellRenderer: ChangeInValueCellRenderer,
            cellRendererParams: {
                hasPercent: true,
                showChangePercent: false,
                hasPinnedColumnValue: false,
            },
        },
        {
            headerName: "Orders",
            field: "orders",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
            },
            hide: true,
        },
        {
            headerName: "Sales",
            field: "sale",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "CTR",
            field: "ctr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
                decimalPlace: 1,
            },
        },
        {
            headerName: gridParams["cpc"]?.headerName || "CPC",
            field: "cpc",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "CPM",
            field: "cpm",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "CR",
            field: "cr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
            },
        },
        {
            headerName: "CPA",
            field: "cpa",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "ACoS",
            field: "acos",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
            },
        },
        {
            headerName: "RoAS",
            field: "roas",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "Top ASIN",
            cellStyle: { "white-space": "normal" },
            field: "top_product",
            filter: "agSetColumnFilter",
            hide: true,

            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            headerName: "% Spend",
            cellStyle: { "white-space": "normal" },
            field: "percent_spend",
            hide: true,

            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "creation_date",
            headerName: "Creation Date",
            filter: "agDateColumnFilter",

            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filterParams: {
                filterOptions: customFilterOptions["date"],
            },
            valueFormatter: (params: ValueFormatterParams) =>
                params.value !== "" ? params.value : "NA",
        },
        {
            field: "match_type",
            headerName: "Match Type",

            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            valueFormatter: (params: ValueFormatterParams) =>
                titleCase(params.value),
            filter: "agTextColumnFilter",
            filterParams: {
                showTooltips: true,
            },
        },
        {
            field: "record_type",
            headerName: "Keyword Type",
            valueFormatter: FilterTextFormatter,

            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filter: "agSetColumnFilter",
            filterParams: {
                showTooltips: true,
                valueFormatter: FilterTextFormatter,
                values: ["keyword", "target"],
            },
        },
        {
            field: "program_type",
            headerName: "Ad Type",
            filter: "agSetColumnFilter",
            cellClass: "text-center",
            valueFormatter: (params: ValueFormatterParams) =>
                params.value ? params.value.toUpperCase() : params.value,
            cellRenderer: ProgramTypeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filterParams: {
                valueFormatter: FilterTextFormatter,
            },
            minWidth: 100,
        },
        {
            field: "campaign_name",
            headerName: "Campaign",
            filter: "agTextColumnFilter",
            cellRenderer: StatusBadgeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isBadgeRequired: !!gridParams?.isBadgeRequired,
                isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired,
            },
        },
        {
            field: "sys_campaign_id",
            filter: "agSetColumnFilter",
            hide: true,

            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "sys_adgroup_id",
            filter: "agSetColumnFilter",
            hide: true,

            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "asin_id",
            filter: "agSetColumnFilter",
            hide: true,

            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "targetingtype",
            headerName: "Campaign Type",
            filter: "agSetColumnFilter",
            hide: true,
            maxWidth: 180,
            minWidth: 170,
            cellRenderer: ProgramTypeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filterParams: {
                valueFormatter: FilterTextFormatter,
            },
        },
    ];

    const productsModalColDefs: ColDef[] = [
        {
            field: "keyword_name",
            headerName: "Target",
            minWidth: 200,
            filter: "agTextColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["text"],
            },
            pinned: "left",
            lockPinned: true,
            cellClass: "lock-pinned",
            tooltipField: "keyword_name",
            cellRenderer: StatusBadgeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                pinnedColumnValue: "Total",
                isBadgeRequired: !!gridParams?.isBadgeRequired,
            },
        },
        {
            field: "bid",
            headerName: "Bid",
            pinned: "left",
            minWidth: isPropPresent(gridParams, "bid")
                ? gridParams["bid"].minWidth
                : 150,
            maxWidth: isPropPresent(gridParams, "bid")
                ? gridParams["bid"].maxWidth
                : 170,
            lockPinned: true,
            filter: false,
            //@ts-ignore
            tooltipValueGetter: ({
                data: { sys_ams_keyword_id, bid },
            }: {
                data: {
                    sys_ams_keyword_id: string;
                    bid: number;
                };
            }) => {
                if (sys_ams_keyword_id === "00000000000000")
                    return `${bid} !operation unavailable`;
                else return bid;
            },
            cellClass: "lock-pinned",
            cellRenderer: isPropPresent(gridParams, "bid")
                ? gridParams["bid"].cellRenderer
                : UpdateBidOrBudgetCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isModalComponent: true,
            },
        },
        {
            field: "state",
            headerName: "Bid Status",
            pinned: "left",
            maxWidth: 100,
            lockPinned: true,
            cellRenderer: (params: ICustomCellRendererParams) =>
                StatusCellRenderer(params, "keyword"),
            cellClass: "lock-pinned",
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isModalComponent: true,
            },
        },
        {
            field: "kwd_class",
            headerName: "Keyword Class",
            filter: "agSetColumnFilter",
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            valueFormatter: (params: ValueFormatterParams) =>
                params.value ? titleCase(params.value) : params.value,
            filterParams: {
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value ? titleCase(params.value) : params.value,
            },
        },
        {
            field: "percent_spend_in_adgroup",
            headerName: "% Spend",
            maxWidth: 150,
            pinned: "left",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,
            cellRenderer: ChangeInValueCellRenderer,
            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            field: "rank",
            headerName: "Organic Rank",
            maxWidth: 150,
            pinned: "left",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: false,
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "ad_group_name",
            headerName: "Ad Group",
            filter: "agTextColumnFilter",
            cellRenderer: StatusBadgeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isBadgeRequired: !!gridParams?.isBadgeRequired,
            },
        },
        {
            headerName: "Impressions",
            field: "impression",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
                hasPinnedColumnValue: true,
            },
            hide: true,
        },
        {
            headerName: gridParams["click"]?.headerName || "Clicks",
            field: "click",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
                hasPinnedColumnValue: true,
            },
            hide: true,
        },
        {
            headerName: "Spends",
            field: "spend",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            sort: "desc",
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "Orders",
            field: "orders",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
                hasPinnedColumnValue: true,
            },
            hide: true,
        },
        {
            headerName: "Sales",
            field: "sale",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "Total Ad Sales",
            cellStyle: { whiteSpace: "normal" },
            field: "total_clicked_ad_sales",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            field: "troas",
            headerName: "TRoAS",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 140,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "CTR",
            field: "ctr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: gridParams["cpc"]?.headerName || "CPC",
            field: "cpc",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            hide: true,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "CPM",
            field: "cpm",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "CR",
            field: "cr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            hide: true,
            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "CPA",
            field: "cpa",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },

            hide: true,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "ACoS",
            field: "acos",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "RoAS",
            field: "roas",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            hide: true,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            field: "match_type",
            headerName: "Match Type",
            valueFormatter: (params: ValueFormatterParams) =>
                titleCase(params.value),
            filter: "agTextColumnFilter",
            filterParams: {
                showTooltips: true,
            },
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "record_type",
            headerName: "Keyword Type",
            valueFormatter: FilterTextFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filter: "agSetColumnFilter",
            filterParams: {
                showTooltips: true,
                valueFormatter: FilterTextFormatter,
                values: ["keyword", "target"],
            },
            hide: true,
        },
        {
            field: "program_type",
            headerName: "Ad Type",
            filter: "agSetColumnFilter",
            cellClass: "text-center",
            valueFormatter: (params: ValueFormatterParams) =>
                params.value ? params.value.toUpperCase() : params.value,
            cellRenderer: ProgramTypeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filterParams: {
                valueFormatter: FilterTextFormatter,
            },
            minWidth: 100,
            hide: true,
        },
        {
            field: "campaign_name",
            headerName: "Campaign",
            filter: "agTextColumnFilter",
            cellRenderer: StatusBadgeCellRenderer,
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isBadgeRequired: !!gridParams?.isBadgeRequired,
                isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired,
            },
        },
        {
            field: "targetingtype",
            headerName: "Campaign Type",
            filter: "agSetColumnFilter",
            hide: true,
            maxWidth: 180,
            minWidth: 170,
            cellRenderer: ProgramTypeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filterParams: {
                valueFormatter: FilterTextFormatter,
            },
        },
    ];

    const campaignModalColDefs: ColDef[] = [
        {
            field: gridParams.keywordColumn || "keyword_name",
            headerName: "Search Term",
            minWidth: 200,
            filter: "agTextColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["text"],
                valueFormatter: function (params: ValueFormatterParams) {
                    return titleCase(params.value || "");
                },
            },
            pinned: "left",
            lockPinned: true,
            cellClass: "lock-pinned",
            checkboxSelection: true,
            tooltipField: gridParams.keywordColumn || "keyword_name",
            valueFormatter: function (params: ValueFormatterParams) {
                return titleCase(params.value || "");
            },
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            cellRenderer: StatusBadgeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                pinnedColumnValue: "Total",
                isBadgeRequired: !!gridParams?.isBadgeRequired,
            },
        },
        {
            headerName: gridParams.bidColumn
                ? gridParams[gridParams.bidColumn].headerName
                : "CPC",
            field: gridParams.bidColumn || "cpc",
            filter: "agNumberColumnFilter",
            pinned: "left",
            lockPinned: true,
            cellClass: "lock-pinned",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: gridParams.bidColumn
                ? gridParams[gridParams.bidColumn].minWidth
                : 170,
            maxWidth: gridParams.bidColumn
                ? gridParams[gridParams.bidColumn].maxWidth
                : 180,
            cellRenderer: KeywordAddBidCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                hasPercent: false,
                showChangePercent: false,
                decimalPlace: 0,
                origin: "smart-to-exact",
            },
        },
        {
            field: "campaign_name",
            headerName: "Campaign",
            filter: "agTextColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["text"],
            },
            cellRenderer: StatusBadgeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isBadgeRequired: !!gridParams?.isBadgeRequired,
                isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired,
            },
        },
        {
            field: "ad_group_name",
            headerName: "Ad Group",
            filter: "agTextColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["text"],
            },
            cellRenderer: StatusBadgeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isBadgeRequired: !!gridParams?.isBadgeRequired,
                isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired,
            },
        },
        {
            field: "match_type",
            headerName: "Match Type",
            filter: "agSetColumnFilter",
            valueFormatter: (params: ValueFormatterParams) =>
                titleCase(params.value),
            cellRenderer: ProgramTypeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            minWidth: 100,
        },
        {
            headerName: "Spends",
            field: "spend",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            sort: "desc",
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "Orders",
            field: "orders",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
            },
            hide: true,
        },
        {
            headerName: "Sales",
            field: "sale",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "Impressions",
            field: "impression",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
            },
        },
        {
            headerName: gridParams["click"]?.headerName || "Clicks",
            field: "click",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
            },
            hide: true,
        },
        {
            headerName: "CTR",
            field: "ctr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
                decimalPlace: 1,
            },
        },
        {
            headerName: "CPM",
            field: "cpm",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "CR",
            field: "cr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
            },
        },
        {
            headerName: "CPA",
            field: "cpa",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "ACoS",
            field: "acos",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
            },
        },
        {
            headerName: "RoAS",
            field: "roas",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "Total Ad Sales",
            cellStyle: { whiteSpace: "normal" },
            field: "total_clicked_ad_sales",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,
            cellRenderer: ChangeInValueCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            field: "troas",
            headerName: "TRoAS",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            minWidth: 140,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
    ];

    const adGroupModalColDefs: ColDef[] = [
        {
            field: "campaign_name",
            headerName: "Campaign",
            minWidth: 150,
            tooltipField: "campaign_name",
            filter: "agTextColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["text"],
            },
            sort: "asc",
            sortIndex: 1,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                pinnedColumnValue: "Total",
            },
        },
        {
            field: "ad_group_name",
            headerName: "Ad Group",
            minWidth: 150,
            tooltipField: "ad_group_name",
            filter: "agTextColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["text"],
            },
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "sku_id",
            headerName: "SKU",
            minWidth: 100,
            cellRenderer: StatusBadgeCellRenderer,
            filter: "agTextColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["text"],
            },
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isBadgeRequired: !!gridParams?.isBadgeRequired,
            },
        },
        {
            field: "state",
            headerName: "Status",
            maxWidth: 100,
            cellRenderer: (params: ICustomCellRendererParams) =>
                StatusCellRenderer(params, "productad"),
            filter: "agSetColumnFilter",
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isModalComponent: true,
            },
        },
        {
            field: "",
            headerName: "Remove SKU",
            maxWidth: 100,
            cellRenderer: RemoveEntityCellRenderer,
            filter: false,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isModalComponent: true,
            },
        },
        {
            field: "percent_spend",
            headerName: "% Spend",
            maxWidth: 150,
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                hasPinnedColumnValue: false,
            },
        },
        {
            headerName: "Clicks",
            field: "click",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "Spends",
            field: "spend",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            sort: "desc",
            sortIndex: 0,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "Impressions",
            field: "impression",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "Orders",
            field: "orders",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                decimalPlace: 0,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "Sales",
            field: "sale",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "CTR",
            field: "ctr",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "CPC",
            field: "cpc",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "CR",
            field: "cr",
            filter: "agNumberColumnFilter",
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            minWidth: 140,
            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "CPA",
            field: "cpa",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "ACoS",
            field: "acos",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "RoAS",
            field: "roas",
            filter: "agNumberColumnFilter",
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
    ];

    const activeColDefs = useMemo(() => {
        return activeModule === ParentModule.KEYWORD_ANALYTICS
            ? keywordModalType === "campaigns-modal"
                ? campaignModalColDefs
                : keywordsModalColDefs
            : activeModule === ParentModule.PRODUCT_ANALYTICS
            ? productModalType === ProductModalType.PRODUCTS_MODAL
                ? productsModalColDefs
                : adGroupModalColDefs
            : null;
        // eslint-disable-next-line
    }, [activeModule, keywordModalType, productModalType]);

    const columnDefs = useGetModulesData({
        type: "columns",
        subGridType: getGridType(),
        colDefs: activeColDefs,
    });

    //   -------------- Keywords Data ---------------------
    const populateReviewKeywordsData = async () => {
        try {
            const data = await createDatasource("keywords", {
                keywords: keywords,
                dateObj: getDateObj(),
            });
            return data;
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    };

    //   -------------- Campaigns Data ---------------------
    const populateSearchTermCampaignsData = async () => {
        try {
            const data = await createDatasource("search-term-campaigns", {
                campaigns: searchTermCampaigns,
                searchTerm: customModalTitle,
                dateObj: getDateObj(),
            });
            return data;
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    };

    //   -------------- Adgroups Data ---------------------
    const populateReviewAdgroupsData = async () => {
        try {
            const data = await createDatasource("product-analytics-adgroups", {
                asin: asin,
                dateObj: getDateObj(),
            });
            return data;
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    };

    //   -------------- Products Data ---------------------
    const populateReviewProductsData = async () => {
        try {
            const data = await createDatasource("product-analytics-keywords", {
                asin,
                dateObj: getDateObj(),
            });
            return data;
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    };

    useEffect(() => {
        setLoading(true);
        if (activeModule === ParentModule.KEYWORD_ANALYTICS) {
            if (keywordModalType === "campaigns-modal") {
                if (searchTermCampaigns.length) {
                    populateSearchTermCampaignsData()
                        .then((campaign) => {
                            setRowData(campaign as TCommonRowData);
                            setLoading(false);
                        })
                        .catch((err) => {
                            console.error(err);
                            Sentry.captureException(err);
                        });
                }
            } else {
                if (keywords.length > 0) {
                    populateReviewKeywordsData()
                        .then((keyword) => {
                            setRowData(keyword as TCommonRowData);
                            setLoading(false);
                        })
                        .catch((err) => {
                            console.error(err);
                            Sentry.captureException(err);
                        });
                }
            }
        } else if (activeModule === ParentModule.PRODUCT_ANALYTICS) {
            if (productModalType === ProductModalType.PRODUCTS_MODAL) {
                if (asin !== "") {
                    populateReviewProductsData()
                        .then((products) => {
                            setRowData(products as TCommonRowData);
                            setLoading(false);
                        })
                        .catch((err) => {
                            console.error(err);
                            Sentry.captureException(err);
                        });
                }
            } else {
                if (asin !== "") {
                    populateReviewAdgroupsData()
                        .then((products) => {
                            setRowData(products as TCommonRowData);
                            setLoading(false);
                        })
                        .catch((err) => {
                            console.error(err);
                            Sentry.captureException(err);
                        });
                }
            }
        }
        // eslint-disable-next-line
    }, [asin, keywords]);

    const isRowSelectable = (rowNode: RowNode) => {
        if (
            activeModule === ParentModule.KEYWORD_ANALYTICS &&
            keywordModalType === "campaigns-modal"
        ) {
            if (isSearchTermExact) return false;
            if (
                !KEYWORD_MATCH_TYPES.includes(
                    rowNode.data.match_type.toLowerCase()
                ) &&
                isNull(isTarget(rowNode.data.keyword_name))
            )
                return false;

            return !DISABLED_MATCH_TYPES.includes(
                rowNode.data.match_type.toLowerCase()
            );
        }
        return true;
    };

    useEffect(() => {
        setIsDisabled(!addAsExactData.length);
    }, [addAsExactData]);

    // !NOTE: MAINTENANCE
    const HeaderComponent = () => {
        return (
            <>
                <div className="d-flex justify-content-between">
                    <div className="text-left" style={{ fontSize: "1rem" }}>
                        {(productModalType === ProductModalType.ADGROUPS_MODAL
                            ? titleCase(customModalTitle)
                            : "Review - ") + titleCase(customModalTitle)}
                        {channel === Channels.AMAZON &&
                        (productModalType === ProductModalType.ADGROUPS_MODAL ||
                            productModalType ===
                                ProductModalType.PRODUCTS_MODAL) ? (
                            <div
                                className="anchor-class"
                                onClick={() =>
                                    window.open(
                                        `https://www.amazon.in/dp/${asin}`
                                    )
                                }
                            >
                                - {asin}
                            </div>
                        ) : null}
                    </div>
                    {keywordModalType !== "campaigns-modal" && (
                        <div className="d-flex">
                            <KeywordsBulkOperationsBtns />

                            {productModalType !==
                            ProductModalType.ADGROUPS_MODAL ? (
                                <div
                                    style={{
                                        width: "5.5rem",
                                        marginRight: "30px",
                                        height: "30px",
                                    }}
                                >
                                    <ExportGridBtn
                                        onClickFunction={() =>
                                            agBtnExport(gridApi)
                                        }
                                        isSubModule={true}
                                    />
                                </div>
                            ) : null}

                            {/* <div id="bulk-operations-pa" style={{display:"none"}}>
                      <button id="disable-complete-product-ads" type = "button" className = "btn btn-sm btn-outline-danger pr-1" onClick={() => bulkActionChangeSubmit("paused", "product-analytics")}>
                      Pause
                      </button>
                      <button id="enable-complete-product-ads" type = "button" className = "btn btn-sm btn-outline-success" onClick={() => bulkActionChangeSubmit("enabled", "product-analytics")}>
                      Enable
                      </button>
                  </div> */}
                        </div>
                    )}
                </div>
                <div className="my-3">
                    <div id="bulk-operations-pa" style={{ display: "none" }}>
                        <button
                            id="disable-complete-product-ads"
                            type="button"
                            className="btn btn-sm btn-outline-danger pr-1 mx-1"
                            style={{
                                display: gridParams.isRemoveSkuAllowed
                                    ? "none"
                                    : "unset",
                            }}
                            onClick={() =>
                                bulkActionChangeSubmit(
                                    "paused",
                                    "product-analytics"
                                )
                            }
                        >
                            Pause <FaPause className="mx-1" />
                        </button>
                        <button
                            id="enable-complete-product-ads"
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            style={{
                                display: gridParams.isRemoveSkuAllowed
                                    ? "none"
                                    : "unset",
                            }}
                            onClick={() =>
                                bulkActionChangeSubmit(
                                    "enabled",
                                    "product-analytics"
                                )
                            }
                        >
                            Enable <FaPlay className="mx-1" />
                        </button>
                        <button
                            id="remove-complete-product-ads"
                            type="button"
                            className="btn btn-sm btn-outline-danger pr-1 mx-1"
                            style={{
                                display: gridParams.isRemoveSkuAllowed
                                    ? "unset"
                                    : "none",
                            }}
                            onClick={() =>
                                bulkActionChangeSubmit(
                                    "archived",
                                    "product-analytics"
                                )
                            }
                        >
                            Remove SKUs <FaTrashAlt className="mx-1" />
                        </button>
                    </div>
                </div>
            </>
        );
    };

    return (
        <>
            <Modal
                title={<HeaderComponent />}
                open={show}
                onCancel={handleClose}
                footer={[
                    <Button
                        hidden={!gridParams.doesAddToExact}
                        disabled={isDisabled}
                        loading={exactLoader}
                        type="primary"
                        style={{
                            background: !isDisabled
                                ? ColorHex.SUCCESS
                                : ColorHex.WHITE,
                        }}
                        id="review-selection-grid-btn"
                        className="me-2"
                        onClick={() =>
                            handleAddExactBtnClick({
                                matchType: "exact",
                                addAsExactData,
                                addAsExactConfig,
                                bidField: "cpc",
                                handleClose,
                            })
                        }
                    >
                        Add as Exact
                    </Button>,

                    <Button
                        hidden={!gridParams.doesAddToExact}
                        disabled={isDisabled}
                        loading={negativeLoader}
                        className="me-2"
                        id="keyword-add-btn"
                        type="primary"
                        style={{
                            background: !isDisabled
                                ? ColorHex.DANGER
                                : ColorHex.WHITE,
                        }}
                        onClick={() =>
                            handleAddExactBtnClick({
                                matchType: "negativeExact",
                                addAsExactData,
                                addAsExactConfig,
                                handleClose,
                            })
                        }
                    >
                        Add as Negative Exact
                    </Button>,
                    <button
                        className="btn btn-secondary"
                        type="button"
                        data-bs-dismiss="modal"
                        onClick={handleClose}
                    >
                        Close
                    </button>,
                ]}
                closeIcon={<AiOutlineClose style={{ fontSize: "35px" }} />}
                style={{ top: 20 }}
                width="95vw"
                styles={{
                    body: {
                        height: "74vh",
                        overflowY: "auto",
                    },
                }}
            >
                <div
                    id="review-container"
                    className="modal-box modal-box-content"
                >
                    <div className="wrapper">
                        <div
                            id="review-grid"
                            className="ag-theme-alpine modal-box-content modal-box"
                            style={{ height: `${gridHeight + 13}px` }}
                        >
                            <AgGridModalLayout
                                {...{
                                    rowData,
                                    columnDefs,
                                    gridParams,
                                    setGridApi,
                                    setGridHeight,
                                    loading,
                                }}
                                onSelectionChanged={
                                    activeModule ===
                                    ParentModule.KEYWORD_ANALYTICS
                                        ? keywordModalType === "campaigns-modal"
                                            ? onSearchTermCampaignSelectionChanged
                                            : onKeywordSelectionChanged
                                        : onProductAdGroupsSelectionChanged
                                }
                                isRowSelectable={isRowSelectable}
                                isTotalRowsReq={true}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default CustomModal;
